<script setup>
import { addKeyframes, DIGIT_IDLE, DIGIT_OFF, DIGIT_OFF_SECONDS, DIGIT_ON, DIGIT_ON_FLARE_END, DIGIT_ON_SECONDS, IGNITE_SECONDS, IGNITE_STAGGER, NEON_IGNITE } from './animation'

const gsapTimeline = useGsapTimeline()

const props = defineProps({
  digit: { type: Number, default: 0 },
  // A symbol instead of a digit: '.' for a thousands separator, '-' when the number is not available.
  symbol: { type: String, default: null },
  // Neon is lit: the digit flickers on and now and then flickers briefly.
  lit: { type: Boolean, default: false },
  // Position in the number: digits ignite and change one after the other from left to right.
  index: { type: Number, default: 0 },
})

// Same structure, so GSAP can tween between them.
const GLOW = 'drop-shadow(0 0 0.02em rgba(237,54,36,1)) drop-shadow(0 0 0.08em rgba(237,54,36,1)) drop-shadow(0 0 0.22em rgba(237,54,36,0))'
const FLARE = 'drop-shadow(0 0 0.03em rgba(255,255,255,1)) drop-shadow(0 0 0.1em rgba(237,54,36,1)) drop-shadow(0 0 0.22em rgba(237,54,36,1))'

const shown = ref(props.digit)
const char = computed(() => props.symbol ?? shown.value)
const litFace = ref(null)

let animation = null
let idle = null

const stop = () => {
  animation?.kill()
  idle?.kill()
}

// `delay` is in seconds from now.
const startIdle = (delay) => {
  idle = gsapTimeline({ repeat: -1, delay })
  addKeyframes(idle, litFace, 'opacity', DIGIT_IDLE, 16 + props.index * 3.3)
}

const addTurnOn = (timeline, position) => {
  const flareEnd = (DIGIT_ON_FLARE_END / 100) * DIGIT_ON_SECONDS
  addKeyframes(timeline, litFace, 'opacity', DIGIT_ON, DIGIT_ON_SECONDS, position)
  timeline.set(litFace, { filter: FLARE }, position)
  timeline.fromTo(litFace, { filter: FLARE }, { filter: GLOW, duration: DIGIT_ON_SECONDS - flareEnd, ease: 'none', immediateRender: false }, position + flareEnd)
  startIdle(position + DIGIT_ON_SECONDS)
}

const ignite = () => {
  stop()
  const delay = props.index * IGNITE_STAGGER
  animation = gsapTimeline({ delay })
  addKeyframes(animation, litFace, 'opacity', NEON_IGNITE, IGNITE_SECONDS)
  startIdle(delay + IGNITE_SECONDS)
}

// A changed digit sputters off, the new one sputters on in its place, like a neon sign being re-wired.
const change = () => {
  if (!props.lit) {
    shown.value = props.digit
    return
  }

  stop()
  const switchAt = DIGIT_OFF_SECONDS + props.index * 0.1
  animation = gsapTimeline()
  addKeyframes(animation, litFace, 'opacity', DIGIT_OFF, DIGIT_OFF_SECONDS)
  animation.call(() => (shown.value = props.digit), null, switchAt)
  addTurnOn(animation, switchAt)
}

watch(
  () => props.lit,
  (lit) => {
    if (lit) {
      ignite()
    } else {
      stop()
      shown.value = props.digit
    }
  }
)
watch(() => [props.digit, props.symbol], change)

// A digit that is added to a number that is already lit switches on by itself.
onMounted(() => {
  if (props.lit) {
    stop()
    animation = gsapTimeline()
    addTurnOn(animation, 0)
  }
})
</script>

<template>
  <span class="neon-digit" :class="{ 'neon-digit--dot': symbol === '.' }" aria-hidden="true">
    <span class="neon-digit__face">{{ char }}</span>
    <span ref="litFace" class="neon-digit__face neon-digit__face--lit" :style="{ filter: GLOW }">{{ char }}</span>
  </span>
</template>

<style scoped>
.neon-digit {
  position: relative;
  display: inline-block;
  width: 0.4em;
  height: 1.1em;
  vertical-align: top;
  text-align: center;
}

.neon-digit--dot {
  width: 0.2em;
}

/* The unlit glass tube: a dim outline. */
.neon-digit__face {
  position: absolute;
  top: 0.1275em;
  left: 0;
  width: 100%;
  height: 0.845em;
  line-height: 0.845em;
  color: transparent;
  -webkit-text-stroke: 0.025em rgb(255 255 255 / 22%);
}

/* The lit neon: bright outline with glow. Its opacity is what flickers. */
.neon-digit__face--lit {
  -webkit-text-stroke-color: #fff;
  opacity: 0;
}
</style>
